/**
 * Unified metadata service for shape-level XML, presentation registry,
 * and ContextResolver storage.
 */

import { getContextResolver } from '../main/core/contextResolver';
import { registerUsedId } from '../main/utils/idGenerator';
import { getOrCreatedocumentId } from './documentIdManager';
import {
    getShapeMetadata,
    setShapeMetadata,
    removeShapeMetadata,
    findAllCarouselShapes,
    type CarouselShapeMetadata,
} from './shapeMetadataService';
import {
    createEmptyPresentationMetadata,
    loadCachedPresentationMetadata,
    loadPresentationMetadataFromPptx,
    savePresentationMetadataToPptx,
} from './presentationMetadataService';
import type {
    MetadataEntry,
    MetadataMapTarget,
    MetadataEntryType,
    PresentationMetadataRegistry,
} from './metadataTypes';

export interface MetadataEntryInput {
    type: MetadataEntryType;
    /** Optional context reference to the original source (as_*, page_*, excelread_*, artifact_*) */
    sourceContextId: string;
    mapsTo?: MetadataMapTarget[];
    payload?: Record<string, unknown>;
    createdAt?: string;
}

export interface MetadataOperations {
    addEntries?: MetadataEntryInput[];
    /** Source context IDs to remove */
    removeEntryIds?: string[];
}

const MAX_METADATA_SNIPPET_CHARS = 400;

function buildSnippet(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return undefined;
    }
    if (normalized.length <= MAX_METADATA_SNIPPET_CHARS) {
        return normalized;
    }
    return `${normalized.slice(0, MAX_METADATA_SNIPPET_CHARS)}...`;
}

function mergeMissingFields(
    target: Record<string, unknown>,
    source?: Record<string, unknown>
): void {
    if (!source) {
        return;
    }
    for (const [key, value] of Object.entries(source)) {
        if (value !== undefined && !(key in target)) {
            target[key] = value;
        }
    }
}

function normalizeMapsTo(mapsTo?: MetadataMapTarget[]): MetadataMapTarget[] | undefined {
    if (mapsTo === undefined) {
        return undefined;
    }

    if (!Array.isArray(mapsTo)) {
        return undefined;
    }

    if (mapsTo.length === 0) {
        return [];
    }

    const normalized: MetadataMapTarget[] = [];
    for (const target of mapsTo) {
        if (!target || typeof target !== 'object') {
            continue;
        }

        const next: MetadataMapTarget = {};
        if (Array.isArray(target.text)) {
            const textValues = target.text
                .map((value) => typeof value === 'string' ? value.trim() : '')
                .filter((value) => value.length > 0);
            if (textValues.length > 0) {
                next.text = textValues;
            }
        }

        if (typeof target.row === 'number' && Number.isInteger(target.row) && target.row > 0) {
            next.row = target.row;
        }

        if (typeof target.col === 'number' && Number.isInteger(target.col) && target.col > 0) {
            next.col = target.col;
        }

        if (next.text || next.row !== undefined || next.col !== undefined) {
            normalized.push(next);
        }
    }

    return normalized.length > 0 ? normalized : [];
}

function buildGenericCitationPayload(
    sourceContextId: string,
    resolvedContent: unknown
): Record<string, unknown> | undefined {
    if (!resolvedContent) {
        return undefined;
    }

    const payload: Record<string, unknown> = {};
    const sourceType =
        sourceContextId.includes('|excelread_') || sourceContextId.startsWith('excelread_')
            ? 'excelread'
            : sourceContextId.startsWith('page_')
                ? 'page'
                : sourceContextId.startsWith('as_')
                    ? 'alphasense'
                    : 'context';
    payload.sourceType = sourceType;

    if (typeof resolvedContent === 'string') {
        const snippet = buildSnippet(resolvedContent);
        if (snippet) {
            payload.snippet = snippet;
        }
        return Object.keys(payload).length > 0 ? payload : undefined;
    }

    if (typeof resolvedContent !== 'object') {
        return Object.keys(payload).length > 0 ? payload : undefined;
    }

    const obj = resolvedContent as Record<string, unknown>;
    if (typeof obj.id === 'string') {
        payload.contextId = obj.id;
    }

    const explicitContentSnippet = buildSnippet(obj.content);
    if (explicitContentSnippet) {
        payload.snippet = explicitContentSnippet;
    }

    const metadata = (obj.metadata && typeof obj.metadata === 'object')
        ? obj.metadata as Record<string, unknown>
        : undefined;
    if (metadata) {
        for (const key of ['filename', 'filepath', 'location', 'pageNumber', 'sheetName', 'rangeAddress', 'opType', 'identifier']) {
            if (metadata[key] !== undefined) {
                payload[key] = metadata[key];
            }
        }
    }

    if (!payload.snippet && typeof obj.asString === 'string') {
        const asStringSnippet = buildSnippet(obj.asString);
        if (asStringSnippet) {
            payload.snippet = asStringSnippet;
        }
    }

    if (!payload.snippet && obj.data !== undefined) {
        try {
            const preview = buildSnippet(JSON.stringify(obj.data));
            if (preview) {
                payload.dataPreview = preview;
            }
        } catch {
            // Ignore serialization errors; payload is optional.
        }
    }

    return Object.keys(payload).length > 0 ? payload : undefined;
}

async function resolveCitationPayload(
    entry: MetadataEntryInput
): Promise<Record<string, unknown> | undefined> {
    if (entry.type !== 'citation') {
        return entry.payload;
    }

    const payload = { ...(entry.payload || {}) };
    const sourceContextId = entry.sourceContextId;

    const resolver = getContextResolver();
    const resolvedContent = resolver.resolve(sourceContextId)?.content;

    if (sourceContextId?.startsWith('as_') && !payload.citations) {
        const resolvedAs = resolvedContent as { citationsUsed?: Array<{ citationNumber: number; citationFullTitle?: string; citationUrl?: string }> } | undefined;
        if (resolvedAs?.citationsUsed?.length) {
            payload.citations = resolvedAs.citationsUsed.map((item) => ({
                id: item.citationNumber,
                title: item.citationFullTitle,
                url: item.citationUrl,
            }));
        }
    }

    // Generic fallback for any resolvable source context (page_*, excelread_*, artifact_*, etc.)
    // Keep explicit fields from caller payload and only fill missing values.
    mergeMissingFields(payload, buildGenericCitationPayload(sourceContextId, resolvedContent));

    return Object.keys(payload).length > 0 ? payload : undefined;
}

function normalizeMetadataEntry(
    entry: MetadataEntryInput,
    now: string
): MetadataEntry {
    registerUsedId(entry.sourceContextId);

    return {
        sourceContextId: entry.sourceContextId,
        type: entry.type,
        mapsTo: normalizeMapsTo(entry.mapsTo),
        payload: entry.payload,
        createdAt: entry.createdAt || now,
        updatedAt: now,
    };
}

async function loadPresentationRegistry(
    documentId: string,
    preferPptx: boolean
): Promise<PresentationMetadataRegistry> {
    if (preferPptx) {
        const pptx = await loadPresentationMetadataFromPptx(documentId);
        if (pptx) {
            return migrateRegistry(pptx);
        }
    }

    const cached = loadCachedPresentationMetadata(documentId);
    if (cached) {
        return migrateRegistry(cached);
    }

    if (preferPptx) {
        const rebuilt = await rebuildRegistryFromShapes(documentId);
        if (Object.keys(rebuilt.entriesById).length > 0) {
            await savePresentationMetadataToPptx(rebuilt);
        }
        return rebuilt;
    }

    return createEmptyPresentationMetadata(documentId);
}

export async function getPresentationMetadataRegistry(documentId?: string): Promise<PresentationMetadataRegistry> {
    const resolveddocumentId = documentId ?? await getOrCreatedocumentId();
    return loadPresentationRegistry(resolveddocumentId, true);
}

async function rebuildRegistryFromShapes(
    documentId: string
): Promise<PresentationMetadataRegistry> {
    const registry = createEmptyPresentationMetadata(documentId);
    const now = new Date().toISOString();
    const shapes = await findAllCarouselShapes();

    for (const shape of shapes) {
        const entries = collectExistingEntries(shape.metadata);
        if (entries.length === 0) {
            continue;
        }

        for (const entry of entries) {
            registry.entriesById[entry.sourceContextId] = entry;
        }

        registry.shapeIndex[shapeKey(shape.slideIndex, shape.shapeId)] = {
            slideIndex: shape.slideIndex,
            shapeId: shape.shapeId,
            sourceContextIds: entries.map((entry) => entry.sourceContextId),
            updatedAt: now,
        };
    }

    registry.lastUpdated = now;
    return registry;
}

function shapeKey(slideIndex0Based: number, shapeId: string): string {
    return `${slideIndex0Based}:${shapeId}`;
}

function collectExistingEntries(metadata?: CarouselShapeMetadata | null): MetadataEntry[] {
    if (!metadata) return [];
    if (!metadata.entries || metadata.entries.length === 0) {
        return [];
    }

    return metadata.entries
        .map((entry) => migrateLegacyEntry(entry))
        .filter((entry): entry is MetadataEntry => Boolean(entry));
}

function migrateRegistry(registry: PresentationMetadataRegistry): PresentationMetadataRegistry {
    const migratedEntriesById: Record<string, MetadataEntry> = {};
    let updated = false;

    for (const entry of Object.values(registry.entriesById || {})) {
        const migrated = migrateLegacyEntry(entry);
        if (!migrated) {
            continue;
        }
        if (migrated.sourceContextId !== (entry as { sourceContextId?: string }).sourceContextId) {
            updated = true;
        }
        migratedEntriesById[migrated.sourceContextId] = migrated;
    }

    const migratedShapeIndex: PresentationMetadataRegistry['shapeIndex'] = {};
    for (const [key, shapeEntry] of Object.entries(registry.shapeIndex || {})) {
        const legacyIds = (shapeEntry as { metadataIds?: string[] }).metadataIds;
        const sourceContextIds = (shapeEntry as { sourceContextIds?: string[] }).sourceContextIds
            ?? legacyIds?.map((id) => id.replace(/^meta_/, ''))
            ?? [];
        if (legacyIds) {
            updated = true;
        }
        migratedShapeIndex[key] = {
            slideIndex: shapeEntry.slideIndex,
            shapeId: shapeEntry.shapeId,
            sourceContextIds,
            updatedAt: shapeEntry.updatedAt,
        };
    }

    if (!updated) {
        return registry;
    }

    return {
        ...registry,
        entriesById: migratedEntriesById,
        shapeIndex: migratedShapeIndex,
    };
}

function migrateLegacyEntry(entry: Partial<MetadataEntry> & {
    metadataId?: string;
    contextId?: string;
    source?: string;
    sourceContextId?: string;
}): MetadataEntry | null {
    const sourceContextId =
        entry.sourceContextId ||
        (entry.metadataId && entry.metadataId.startsWith('meta_')
            ? entry.metadataId.replace(/^meta_/, '')
            : undefined);

    if (!sourceContextId || !entry.type) {
        return null;
    }

    return {
        sourceContextId,
        type: entry.type,
        mapsTo: normalizeMapsTo(entry.mapsTo),
        payload: entry.payload,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
    };
}

export async function applyShapeMetadata(params: {
    slideId: number;
    shapeId: string;
    documentId?: string;
    metadata?: MetadataOperations;
}): Promise<void> {
    const { slideId, shapeId, metadata } = params;
    if (!metadata || (!metadata.addEntries?.length && !metadata.removeEntryIds?.length)) {
        return;
    }

    const documentId = params.documentId ?? await getOrCreatedocumentId();
    const slideIndex0Based = slideId - 1;
    const now = new Date().toISOString();

    const existing = await getShapeMetadata(slideIndex0Based, shapeId);
    let entries = collectExistingEntries(existing);

    if (metadata.removeEntryIds?.length) {
        const removeSet = new Set(metadata.removeEntryIds);
        entries = entries.filter((entry) => !removeSet.has(entry.sourceContextId));
    }

    if (metadata.addEntries?.length) {
        const existingById = new Map(entries.map((entry) => [entry.sourceContextId, entry]));
        for (const entryInput of metadata.addEntries) {
            const previous = existingById.get(entryInput.sourceContextId);
            const normalized = normalizeMetadataEntry(entryInput, now);
            const resolvedPayload = await resolveCitationPayload(entryInput);
            normalized.payload = resolvedPayload ?? normalized.payload ?? previous?.payload;
            normalized.mapsTo = normalized.mapsTo ?? previous?.mapsTo;
            normalized.createdAt = previous?.createdAt || normalized.createdAt;
            entries.push(normalized);
            existingById.set(normalized.sourceContextId, normalized);
        }
    }

    // Deduplicate by sourceContextId (last write wins)
    if (entries.length > 1) {
        const deduped = new Map<string, MetadataEntry>();
        for (const entry of entries) {
            deduped.set(entry.sourceContextId, entry);
        }
        entries = Array.from(deduped.values());
    }

    if (entries.length === 0) {
        await removeShapeMetadata(slideIndex0Based, shapeId);
        return;
    }

    const nextMetadata: CarouselShapeMetadata = {
        ...(existing || {}),
        entries: entries,
    };

    await setShapeMetadata(slideIndex0Based, shapeId, nextMetadata);

    const registry = await loadPresentationRegistry(documentId, true);
    const key = shapeKey(slideIndex0Based, shapeId);
    const sourceContextIds = entries.map((entry) => entry.sourceContextId);

    for (const entry of entries) {
        if (entry.payload) {
            getContextResolver().storeWithId(entry.sourceContextId, entry.payload);
        } else {
            getContextResolver().storeWithId(entry.sourceContextId, entry);
        }
        registry.entriesById[entry.sourceContextId] = entry;
    }

    registry.shapeIndex[key] = {
        slideIndex: slideIndex0Based,
        shapeId,
        sourceContextIds,
        updatedAt: now,
    };
    registry.lastUpdated = now;

    // Remove unreferenced entries from registry
    const referencedIds = new Set<string>();
    for (const shapeEntry of Object.values(registry.shapeIndex)) {
        for (const id of shapeEntry.sourceContextIds) {
            referencedIds.add(id);
        }
    }
    for (const id of Object.keys(registry.entriesById)) {
        if (!referencedIds.has(id)) {
            delete registry.entriesById[id];
        }
    }

    await savePresentationMetadataToPptx(registry);
}

export async function hydrateMetadataForDecks(params: {
    documentIds: Array<{ documentId: string; preferPptx: boolean }>;
}): Promise<void> {
    const resolver = getContextResolver();
    for (const deck of params.documentIds) {
        const registry = await loadPresentationRegistry(deck.documentId, deck.preferPptx);
        for (const entry of Object.values(registry.entriesById)) {
            const payload = entry.payload ?? entry;
            resolver.storeWithId(entry.sourceContextId, payload);
        }
    }
}

export async function updateSourceFilePathForExcel(
    excelId: string,
    newPath: string,
    documentId?: string
): Promise<void> {
    const resolveddocumentId = documentId ?? await getOrCreatedocumentId();
    const registry = await loadPresentationRegistry(resolveddocumentId, true);
    let modified = false;

    const resolver = getContextResolver();
    for (const entry of Object.values(registry.entriesById)) {
        if (entry.type !== 'artifact') {
            continue;
        }
        const payload = entry.payload as { excelId?: string; source?: { originalFilePath?: string } } | undefined;
        if (payload?.excelId !== excelId || !payload.source) {
            continue;
        }
        payload.source.originalFilePath = newPath;
        entry.payload = payload;
        entry.updatedAt = new Date().toISOString();
        resolver.storeWithId(entry.sourceContextId, payload);
        modified = true;
    }

    if (modified) {
        registry.lastUpdated = new Date().toISOString();
        await savePresentationMetadataToPptx(registry);
        console.log(`[MetadataService] Updated source path for ${excelId}: ${newPath}`);
    }
}

export async function getArtifactEntryByShape(
    shapeId: string,
    documentId?: string
): Promise<MetadataEntry | null> {
    const resolveddocumentId = documentId ?? await getOrCreatedocumentId();
    const registry = await loadPresentationRegistry(resolveddocumentId, true);
    for (const entry of Object.values(registry.shapeIndex)) {
        if (entry.shapeId !== shapeId) continue;
        for (const sourceContextId of entry.sourceContextIds) {
            const candidate = registry.entriesById[sourceContextId];
            if (candidate?.type === 'artifact') {
                return candidate;
            }
        }
    }
    return null;
}

export async function removeMetadataForShape(params: {
    slideId: number;
    shapeId: string;
    documentId?: string;
}): Promise<void> {
    const documentId = params.documentId ?? await getOrCreatedocumentId();
    const slideIndex0Based = params.slideId - 1;
    const key = shapeKey(slideIndex0Based, params.shapeId);

    const registry = await loadPresentationRegistry(documentId, true);
    const removedIds = registry.shapeIndex[key]?.sourceContextIds ?? [];
    delete registry.shapeIndex[key];

    const referencedIds = new Set<string>();
    for (const shapeEntry of Object.values(registry.shapeIndex)) {
        for (const id of shapeEntry.sourceContextIds) {
            referencedIds.add(id);
        }
    }
    for (const id of removedIds) {
        if (!referencedIds.has(id)) {
            delete registry.entriesById[id];
        }
    }

    registry.lastUpdated = new Date().toISOString();
    await savePresentationMetadataToPptx(registry);

    try {
        await removeShapeMetadata(slideIndex0Based, params.shapeId);
    } catch (error) {
        console.warn('[MetadataService] Failed to remove shape XML metadata:', error);
    }
}
